import { environment } from '../../../../environments/environment';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, Type } from '@angular/core';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { Feature, Geometry } from 'geojson';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { MapBoxMapLockControl } from './custom-map-controls/map-box-map-lock-control';
import { MapBoxMapRefocusControl } from './custom-map-controls/map-box-map-refocus-control';
import { MapBoxStyle } from './map-box-style.enum';

export enum MapBoxMode {
  GpsAreaMode,
  LineMode,
  PositionMode
}

export interface MapData {
  data: string;
  mode: MapBoxMode;
}

@Component({
    selector: 'app-map-box-map',
    imports: [ButtonModule],
    templateUrl: './map-box-map.component.html',
    styleUrl: './map-box-map.component.scss'
})
export class MapBoxMapComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input()
  public isReadOnly: boolean = false;

  @Input()
  public isSingleMode: boolean = true;

  @Input()
  public height = '300px';

  @Input()
  public style: MapBoxStyle = MapBoxStyle.Satellite;

  @Input()
  public mapId: string = 'map';

  @Input()
  public set mapData(value: MapData) {
    const mapMode = value?.mode;
    const data = value?.data;
    if (mapMode === this.mapMode && data === this.gpsData) {
      return;
    }

    this.mapMode = mapMode;
    this.gpsData = data;

    if (this.map && (this.map.loaded() || this.mapLoaded)) {
      if (this.gpsData) {
        this.setContentAreaForMap();
      }
      else {
        this.resetMap();
      }
    }
  }

  @Output()
  public mapDataChange = new EventEmitter<MapData>();

  private map: mapboxgl.Map;
  private mapLoaded = false;
  private marker: mapboxgl.Marker;

  //Coordinates for Switzerland
  private lat: number = 46.80121;
  private lng: number = 8.226692;

  private onMapLoadReference: any;
  private handleDrawCreateReference: any;
  private handleDrawUpdateReference: any;
  private handleDrawDeleteReference: any;
  private newPositionSelectedReference: any;
  private geoCoderNewPositionSelectedReference: any;

  private drawControl: MapboxDraw;
  private fullScreenControl: mapboxgl.FullscreenControl;
  private lockControl: MapBoxMapLockControl;
  private refocusControl: MapBoxMapRefocusControl;
  private geoCoderControl: MapboxGeocoder;

  private mapMode: MapBoxMode;
  private gpsData: string;

  constructor(private el: ElementRef,
    private renderer: Renderer2) {
  }

  ngOnInit(): void {
    this.fullScreenControl = new mapboxgl.FullscreenControl()
    this.lockControl = new MapBoxMapLockControl(!!this.gpsData, this.handleLockStatusChange.bind(this));
    this.refocusControl = new MapBoxMapRefocusControl(this.refocusMap.bind(this));
    this.geoCoderControl = new MapboxGeocoder({
      accessToken: environment.mapbox.accessToken,
      mapboxgl: mapboxgl
    });

    // Bind the functions to the class to be able to remove the event listeners later
    // Need to have the same reference content when adding and removing the event listeners
    this.onMapLoadReference = this.handleMapLoad.bind(this);
    this.handleDrawCreateReference = this.handleDrawCreate.bind(this);
    this.handleDrawUpdateReference = this.handleDrawUpdate.bind(this);
    this.handleDrawDeleteReference = this.handleDrawDelete.bind(this);
    this.newPositionSelectedReference = this.newPositionSelected.bind(this);
    this.geoCoderNewPositionSelectedReference = this.geoCoderNewPositionSelected.bind(this);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      // All events for map:
      // https://docs.mapbox.com/mapbox-gl-js/api/map/#map-events
      this.initializeMap();
      this.setHeight();
    }, 100);
  }

  ngOnDestroy(): void {
    this.map.off('load', this.onMapLoadReference);
    this.map.off('draw.delete', this.handleDrawDeleteReference);
    this.map.off('draw.update', this.handleDrawUpdateReference);
    this.map.off('draw.create', this.handleDrawCreateReference);
    this.map.off('click', this.newPositionSelectedReference);
    if (this.map.hasControl(this.geoCoderControl)) {
      this.geoCoderControl?.off('result', this.geoCoderNewPositionSelectedReference);
    }
  }

  public lockMap(): void {
    this.lockControl.setLockState(true);
  }

  private setContentAreaForMap(): void {
    this.lockControl.setLockState(true);
    this.addFeaturesToMap();
    this.updateDrawControls(false);
    this.centerMapToFeature();
    this.handleLockStatusChange();
  }

  private resetMap() {
    this.lockControl.setLockState(false, false);
    if (!this.map) {
      return;
    }
    this.drawControl?.deleteAll();
    this.updateDrawControls(true);
    this.map.setCenter([this.lng, this.lat]);
    this.map.setZoom(7);
    this.updateSearchControl();
    this.handleLockStatusChange();
  }

  private initializeMap() {
    this.map = new mapboxgl.Map({
      accessToken: environment.mapbox.accessToken,
      container: this.mapId,
      style: this.style,
      zoom: 7,
      center: [this.lng, this.lat]
    });
    this.map.on('load', this.onMapLoadReference);

    //Add controls
    this.map.addControl(this.fullScreenControl, 'top-right');
    this.map.addControl(this.refocusControl, 'top-right');
    if (!this.isReadOnly) {
      this.map.addControl(this.lockControl, 'top-right');
    }
    this.updateSearchControl();

    if (this.mapMode == MapBoxMode.PositionMode) {
      this.geoCoderControl.on('result', this.geoCoderNewPositionSelectedReference);
    }
  }

  private handleLockStatusChange() {
    this.updateSearchControl();
    if (this.mapMode == MapBoxMode.GpsAreaMode || this.mapMode == MapBoxMode.LineMode) {
      this.lockControl.isLocked ? this.drawControl?.changeMode(this.StaticModeName) : this.drawControl?.changeMode(MapboxDraw.constants.modes.SIMPLE_SELECT);
      this.updateDrawControls(!this.lockControl.isLocked);
    } else if (this.mapMode == MapBoxMode.PositionMode) {
      this.updatePositionListener();
    }
  }

  private handleMapLoad(): void {
    //Add (zone / polygon) or line functionality if activated
    if (this.mapMode == MapBoxMode.GpsAreaMode || this.mapMode == MapBoxMode.LineMode) {
      this.addMapboxDrawAndHandleChanges();
    }

    //Add position functionality if activated
    if (this.mapMode == MapBoxMode.PositionMode) {
      this.addPositionMarkerAndHandleChanges();
    }
    this.mapLoaded = true;

    this.handleReadyOnlyMode();
  }

  private addMapboxDrawAndHandleChanges(): void {
    //Declare custom mode "static" for mapbox draw
    //https://github.com/mapbox/mapbox-gl-draw-static-mode
    var modes = MapboxDraw.modes;
    (modes as any).static = StaticMode;

    //Doc for mapbox draw
    //https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/API.md
    this.drawControl = new MapboxDraw({
      modes: modes as any,
      displayControlsDefault: false,
      controls: {
        polygon: this.mapMode == MapBoxMode.GpsAreaMode,
        line_string: this.mapMode == MapBoxMode.LineMode,
        point: this.mapMode == MapBoxMode.PositionMode,
        trash: true
      },
      defaultMode: this.StaticModeName
    });

    this.applyStyleToDrawControl();

    this.map.addControl(this.drawControl);

    if (this.gpsData) {
      this.addFeaturesToMap();
      this.updateDrawControls(false);
      this.centerMapToFeature();
    }

    if (this.lockControl.isLocked) {
      this.updateDrawControls(false);
    }

    this.map.on('draw.delete', this.handleDrawDeleteReference);
    this.map.on('draw.update', this.handleDrawUpdateReference);
    this.map.on('draw.create', this.handleDrawCreateReference);
  }

  private handleDrawCreate(args: any): void {
    this.updateFeaturePosition(args);
  }

  private handleDrawUpdate(args: any): void {
    this.updateFeaturePosition(args);
  }

  private handleDrawDelete(): void {
    if (!this.isSingleMode) {
      return;
    }
    this.updateDrawControls(true);
  }

  private updateFeaturePosition(args: any): void {
    if (args?.features?.length > 0) {
      let gpsDataStr = '';
      this.drawControl?.getAll().features.forEach((feature: any, index: number) => {
        gpsDataStr += (index == 0 ? '' : '|') + this.transformToDBCoordinatesString(feature.geometry.coordinates[0]);
      });

      this.gpsData = gpsDataStr;
      this.mapDataChange.emit({ data: this.gpsData, mode: this.mapMode });
      if (this.isSingleMode) {
        this.updateDrawControlButtonVisibility(false && this.mapMode === MapBoxMode.GpsAreaMode, 'mapbox-gl-draw_polygon');
        this.updateDrawControlButtonVisibility(false && this.mapMode === MapBoxMode.LineMode, 'mapbox-gl-draw_line');
      }
    }
  }

  private updateDrawControls(enable: boolean): void {
    if (!enable || !this.isSingleMode || !this.gpsData) {
      // activation is not allowed in single mode if gps data is available
      this.updateDrawControlButtonVisibility(enable && this.mapMode === MapBoxMode.GpsAreaMode, 'mapbox-gl-draw_polygon');
      this.updateDrawControlButtonVisibility(enable && this.mapMode === MapBoxMode.LineMode, 'mapbox-gl-draw_line');
    }

    this.updateDrawControlButtonVisibility(enable, 'mapbox-gl-draw_trash');

    if (!enable) {
      if (this.lockControl.isLocked) {
        this.drawControl?.changeMode(this.StaticModeName);
      }
      else {
        this.drawControl?.changeMode(MapboxDraw.constants.modes.SIMPLE_SELECT);
      }
    } else if (!this.gpsData && !this.isReadOnly) {
      // setting draw polygon can add a weird polygon to the map but only when no features were added before, so we need to remove them afterwards
      let removeFeatures = this.drawControl?.getAll().features.length == 0;
      const drawMode = this.getDrawMode(this.mapMode);
      this.drawControl?.changeMode(drawMode);
      if (removeFeatures) {
        this.drawControl?.deleteAll();
      }
    } else {
      this.drawControl?.changeMode(MapboxDraw.constants.modes.SIMPLE_SELECT);
    }
  }

  private updateDrawControlButtonVisibility(enable: boolean, buttonClass: string): void {
    const drawControls = document.querySelectorAll('#' + this.mapId + ' .' + buttonClass);
    drawControls.forEach(control => {
      (control as HTMLElement).style.display = enable ? 'block' : 'none';
    });
  }

  private getDrawMode(mapBoxMode: MapBoxMode): string {
    switch (mapBoxMode) {
      case MapBoxMode.GpsAreaMode:
        return MapboxDraw.constants.modes.DRAW_POLYGON;
      case MapBoxMode.LineMode:
        return MapboxDraw.constants.modes.DRAW_LINE_STRING;
      case MapBoxMode.PositionMode:
        return MapboxDraw.constants.modes.DRAW_POINT;
      default:
        return MapboxDraw.constants.modes.SIMPLE_SELECT;
    }
  }

  private addFeaturesToMap(): void {
    this.drawControl?.deleteAll();
    if (this.mapMode == MapBoxMode.PositionMode) {
      return;
    }

    const gpsData = this.gpsData;
    const separatedGpsData = this.getAreasAsArray(gpsData);
    separatedGpsData.forEach((gpsData, index) => {
      const feature: Feature<Geometry> = {
        type: 'Feature', // Explicitly set the type to "Feature"
        id: 'feature-id-' + index,
        properties: {},
        geometry: {
          type: this.mapMode == MapBoxMode.GpsAreaMode ? 'Polygon' : 'MultiLineString',
          coordinates: [
            this.transformToGeoJsonCoordinates(gpsData)
          ]
        }
      };
      this.drawControl?.add(feature);
    });
  }

  private setHeight(): void {
    const matchParentElements = this.el.nativeElement.querySelectorAll('#' + this.mapId);
    matchParentElements.forEach(element => {
      this.renderer.setStyle(element, 'height', this.height);
    });
  }

  private transformToGeoJsonCoordinates(coordinatesString: string): number[][] {
    // Split the string by semicolons to get individual coordinate pairs
    const coordinatePairs = coordinatesString.split(';').filter(pair => pair.trim() !== '');

    // Transform the coordinate pairs into the desired array format
    const transformedCoordinates = coordinatePairs.map(pair => {
      const [lat, lng] = pair.split(',').map(Number);
      return [lng, lat];
    });

    return transformedCoordinates;
  }

  private transformToDBCoordinatesString(coordinates: number[][]): string {
    const transformedCoordinates = coordinates.map(pair => pair.reverse().join(',')).join(';');
    return transformedCoordinates;
  }

  private refocusMap(): void {
    if (this.mapMode == MapBoxMode.GpsAreaMode || this.mapMode == MapBoxMode.LineMode) {
      this.centerMapToFeature();
    }
    else if (this.mapMode == MapBoxMode.PositionMode) {
      this.centerMapToPosition();
    }
  }

  private centerMapToFeature(): void {
    if (this.drawControl && this.drawControl.getAll().features.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();

      this.drawControl.getAll().features.forEach(feature => {
        // TODO: check for better implementation maybe use bbox property
        (feature.geometry as any).coordinates[0].forEach((coord: [number, number]) => {
          bounds.extend(coord);
        });
      });

      this.map.fitBounds(bounds, {
        padding: 20
      });
    }
  }

  private updateSearchControl(): void {
    if (this.lockControl.isLocked) {
      if (this.map.hasControl(this.geoCoderControl)) {
        this.map.removeControl(this.geoCoderControl);
      }
    } else {
      if (!this.map.hasControl(this.geoCoderControl)) {
        this.map.addControl(this.geoCoderControl, 'top-left');
      }
    }
  }

  private addPositionMarkerAndHandleChanges(): void {
    if (this.mapMode != MapBoxMode.PositionMode) {
      return;
    }
    if (this.gpsData) {
      const [lat, lng] = this.gpsData.split(',').map(Number);
      this.marker = new mapboxgl.Marker({color: this.AreaColor })
        .setLngLat([lng, lat])
        .addTo(this.map);
      this.centerMapToPosition();
    }
    if (!this.lockControl.isLocked) {
      this.map.on('click', this.newPositionSelectedReference);
    }
  }

  private centerMapToPosition(): void {
    if (this.mapMode != MapBoxMode.PositionMode) {
      return;
    }
    if (this.gpsData) {
      const [lat, lng] = this.gpsData.split(',').map(Number);
      this.map.flyTo({
        center: [lng, lat],
        zoom: 15
      });
    }
  }

  private updatePositionListener(): void {
    if (this.lockControl.isLocked) {
      this.map.off('click', this.newPositionSelectedReference);
    } else {
      this.map.on('click', this.newPositionSelectedReference);
    }
  }

  private geoCoderNewPositionSelected(e: any): void {
    this.gpsData = `${e.result.center[1]},${e.result.center[0]}`;
    this.marker ? this.marker.setLngLat([e.result.center[0], e.result.center[1]]) :
      this.marker = new mapboxgl.Marker({color: this.AreaColor })
        .setLngLat([e.result.center[0], e.result.center[1]])
        .addTo(this.map);

    //this.geoCoderControl.mapMarker.remove();
    this.mapDataChange.emit({data: this.gpsData, mode: this.mapMode});
  }

  private newPositionSelected(e: mapboxgl.MapMouseEvent): void {
    this.gpsData = `${e.lngLat.lat},${e.lngLat.lng}`;
    this.marker ? this.marker.setLngLat([e.lngLat.lng, e.lngLat.lat]) :
      this.marker = new mapboxgl.Marker({color: this.AreaColor })
        .setLngLat([e.lngLat.lng, e.lngLat.lat])
        .addTo(this.map);

    this.geoCoderControl.clear();
    this.mapDataChange.emit({data: this.gpsData, mode: this.mapMode});
  }

  private handleReadyOnlyMode(): void {
    //Remove all controls if map is read only
    if (this.isReadOnly) {
      this.lockControl.setLockState(true);
      this.updateSearchControl();
      this.updatePositionListener();
      this.updateDrawControls(false);
    }
  }

  private applyStyleToDrawControl(): void {
    if (this.mapMode == MapBoxMode.GpsAreaMode) {
      this.setDrawControlStyle('gl-draw-polygon-fill.cold', 'fill-color', 3, this.AreaColor);
      this.setDrawControlStyle('gl-draw-polygon-fill.cold', 'fill-opacity', null, 0.6);
      this.setDrawControlStyle('gl-draw-lines.cold', 'line-color', 3, this.AreaColor);
      this.setDrawControlStyle('gl-draw-polygon-fill.hot', 'fill-color', 3, this.AreaColor);
      this.setDrawControlStyle('gl-draw-polygon-fill.hot', 'fill-opacity', null, 0.6);
      this.setDrawControlStyle('gl-draw-lines.hot', 'line-color', 3, this.AreaColor);
    } else if (this.mapMode == MapBoxMode.LineMode) {
      this.setDrawControlStyle('gl-draw-lines.cold', 'line-color', 3, this.LineColor);
      this.setDrawControlStyle('gl-draw-lines.cold', 'line-width', null, 5);
      this.setDrawControlStyle('gl-draw-lines.hot', 'line-color', 3, this.LineColor);
      this.setDrawControlStyle('gl-draw-lines.hot', 'line-width', null, 5);
    }
  }

  private setDrawControlStyle(styleId: string, paint: string, position: number, value: any): void {
    const style = (this.drawControl as any).options.styles.find(style => style.id === styleId);
    if (style) {
      if (position != null) {
        style.paint[paint][position] = value;
      } else {
        style.paint[paint] = value;
      }
    } else {
      console.warn(`Style with ID ${styleId} not found in draw control styles.`);
    }
  }

  // multiple gps data records can be concatenated with '|'
  private getAreasAsArray(areas: string): string[] {
    if (!areas) {
      return [];
    }
    return areas.split('|').map(area => area.trim()).filter(area => area !== '');
  }

  private readonly StaticModeName: string = MapboxDraw.constants.modes.STATIC ?? 'static'; // should be defined in mapbox draw but not always, so we need fallback
  private readonly AreaColor: string = '#006633';
  private readonly LineColor: string = '#FFDE21';
}
